import { UNIT_TAGS } from "../core/constants.js";
import { ARMOR_CLASSES, WEAPON_CLASSES } from "./weaponClasses.js";

/**
 * All battlefield units use the same normalized stat shape.
 * Optional `levelUpGrowthModifiers` can be added per unit with additive
 * `{ chance, weight, increment: { min, max } }` fields keyed by stat. Shared
 * defaults live in `src/game/simulation/progression.js`.
 */
export const UNIT_CATALOG = {
  grunt: {
    id: "grunt",
    name: "Grunt",
    family: UNIT_TAGS.INFANTRY,
    armorClass: ARMOR_CLASSES.INFANTRY,
    weaponClass: WEAPON_CLASSES.RIFLE,
    cost: 100,
    maxHealth: 100,
    attack: 62,
    armor: 6,
    movement: 4,
    minRange: 1,
    maxRange: 1,
    staminaMax: 60,
    ammoMax: 7,
    luck: 3,
    levelUpGrowthModifiers: {
      armor: { increment: { min: -2, max: -2 } },
      movement: { chance: 11, increment: { min: 1, max: 2 } },
      maxRange: { chance: 4, weight: 1 },
      staminaMax: { chance: -3, weight: -1, increment: { min: -2, max: -3 } },
      ammoMax: { chance: 3, weight: -1, increment: { min: 1, max: 1 } },
      luck: { increment: { max: 1 } },
    },
  },
  breaker: {
    id: "breaker",
    name: "Breaker",
    family: UNIT_TAGS.INFANTRY,
    armorClass: ARMOR_CLASSES.INFANTRY,
    weaponClass: WEAPON_CLASSES.BREAKER_CHARGE,
    cost: 250,
    maxHealth: 100,
    attack: 70,
    armor: 14,
    movement: 4,
    minRange: 1,
    maxRange: 1,
    staminaMax: 60,
    ammoMax: 5,
    luck: 3,
    levelUpGrowthModifiers: {
      armor: { increment: { min: -1, max: -2 } },
      movement: { chance: 11, increment: { min: 1, max: 2 } },
      maxRange: { chance: 2, weight: 1 },
      staminaMax: { chance: 2, increment: { min: -2, max: -3 } },
      ammoMax: { chance: 3, weight: -1, increment: { min: 1, max: 1 } },
    },
  },
  longshot: {
    id: "longshot",
    name: "Longshot",
    family: UNIT_TAGS.INFANTRY,
    armorClass: ARMOR_CLASSES.INFANTRY,
    weaponClass: WEAPON_CLASSES.MARKSMAN_RIFLE,
    cost: 300,
    maxHealth: 100,
    attack: 62,
    armor: 14,
    movement: 3,
    minRange: 2,
    maxRange: 3,
    staminaMax: 50,
    ammoMax: 6,
    luck: 3,
    levelUpGrowthModifiers: {
      attack: { chance: 8, weight: 1, increment: { min: 1, max: 0 } },
      armor: { chance: -18, weight: -2, increment: { min: -2, max: -3 } },
      maxHealth: { chance: -23, weight: -3, increment: { min: -3, max: -4 } },
      movement: { chance: 2, weight: 1 },
      maxRange: { chance: 6, weight: 1 },
      staminaMax: { chance: -8, increment: { min: 1, max: 0 } },
      ammoMax: { chance: 3 },
      luck: { chance: 1, weight: 1, increment: { min: 1, max: 1 } },
    },
  },
  medic: {
    id: "medic",
    name: "Medic",
    family: UNIT_TAGS.INFANTRY,
    armorClass: ARMOR_CLASSES.INFANTRY,
    weaponClass: WEAPON_CLASSES.SIDEARM,
    cost: 500,
    maxHealth: 100,
    attack: 50,
    armor: 6,
    movement: 4,
    minRange: 1,
    maxRange: 1,
    staminaMax: 60,
    ammoMax: 7,
    luck: 2,
    levelUpGrowthModifiers: {
      attack: { chance: -32, weight: -3, increment: { min: -3, max: -3 } },
      armor: { chance: 12, weight: 1, increment: { min: -1, max: -2 } },
      maxHealth: { chance: -3, increment: { min: -1, max: -2 } },
      movement: { chance: -2 },
      maxRange: { chance: -1 },
      staminaMax: { chance: -8, weight: 1, increment: { min: -2, max: -1 } },
      ammoMax: { chance: -17, weight: -1, increment: { max: -1 } },
      luck: { chance: 4, weight: -1, increment: { min: 1, max: 1 } },
    },
  },
  mechanic: {
    id: "mechanic",
    name: "Mechanic",
    family: UNIT_TAGS.INFANTRY,
    armorClass: ARMOR_CLASSES.INFANTRY,
    weaponClass: WEAPON_CLASSES.TOOL_RIFLE,
    cost: 600,
    maxHealth: 100,
    attack: 54,
    armor: 7,
    movement: 4,
    minRange: 1,
    maxRange: 1,
    staminaMax: 60,
    ammoMax: 7,
    luck: 2,
    levelUpGrowthModifiers: {
      attack: { chance: -32, weight: -3, increment: { min: -3, max: -3 } },
      armor: { chance: 12, weight: 1, increment: { min: -1, max: -2 } },
      maxHealth: { chance: -3, increment: { min: -1, max: -2 } },
      movement: { chance: -2 },
      maxRange: { chance: -1 },
      staminaMax: { chance: -8, weight: 1, increment: { min: -2, max: -1 } },
      ammoMax: { chance: -17, weight: -1, increment: { max: -1 } },
      luck: { chance: 4, weight: -1, increment: { min: 1, max: 1 } },
    },
  },
  runner: {
    id: "runner",
    name: "Runner",
    family: UNIT_TAGS.VEHICLE,
    armorClass: ARMOR_CLASSES.LIGHT,
    weaponClass: WEAPON_CLASSES.AUTOCANNON,
    cost: 400,
    maxHealth: 100,
    attack: 72,
    armor: 45,
    movement: 6,
    minRange: 1,
    maxRange: 1,
    staminaMax: 80,
    ammoMax: 7,
    luck: 3,
    levelUpGrowthModifiers: {
      attack: { chance: -2, weight: 1, increment: { min: 1, max: 1 } },
      armor: { chance: -8 },
      maxHealth: { chance: -3, weight: -1, increment: { min: -2, max: -1 } },
      movement: { chance: 4, weight: 2, increment: { max: 1 } },
      maxRange: { chance: 1 },
      staminaMax: { chance: 7, weight: 1, increment: { min: 1, max: 2 } },
      ammoMax: { chance: 3, increment: { min: 1, max: 1 } },
      luck: { chance: 2, weight: -1 },
    },
  },
  bruiser: {
    id: "bruiser",
    name: "Bruiser",
    family: UNIT_TAGS.VEHICLE,
    armorClass: ARMOR_CLASSES.MEDIUM,
    weaponClass: WEAPON_CLASSES.BRUISER_CANNON,
    cost: 700,
    maxHealth: 100,
    attack: 84,
    armor: 55,
    movement: 5,
    minRange: 1,
    maxRange: 1,
    staminaMax: 70,
    ammoMax: 6,
    luck: 3,
  },
  juggernaut: {
    id: "juggernaut",
    name: "Juggernaut",
    family: UNIT_TAGS.VEHICLE,
    armorClass: ARMOR_CLASSES.HEAVY,
    weaponClass: WEAPON_CLASSES.HEAVY_CANNON,
    cost: 2000,
    maxHealth: 100,
    attack: 115,
    armor: 90,
    movement: 4,
    minRange: 1,
    maxRange: 1,
    staminaMax: 55,
    ammoMax: 6,
    luck: 2,
    levelUpGrowthModifiers: {
      armor: { chance: -3, weight: 1, increment: { min: 2, max: 2 } },
      maxHealth: { chance: 2, weight: 1, increment: { min: 2, max: 1 } },
      movement: { chance: -4, increment: { min: -1, max: -1 } },
      maxRange: { increment: { max: 2 } },
      ammoMax: { chance: 3, increment: { min: 1, max: 1 } },
      luck: { chance: -8, weight: -2, increment: { max: -1 } },
    },
  },
  "siege-gun": {
    id: "siege-gun",
    name: "Siege Gun",
    family: UNIT_TAGS.VEHICLE,
    armorClass: ARMOR_CLASSES.LIGHT,
    weaponClass: WEAPON_CLASSES.SIEGE_ARTILLERY,
    cost: 600,
    maxHealth: 100,
    attack: 95,
    armor: 38,
    movement: 3,
    minRange: 2,
    maxRange: 3,
    staminaMax: 60,
    ammoMax: 6,
    luck: 3,
    levelUpGrowthModifiers: {
      attack: { increment: { min: 1, max: 0 } },
      armor: { chance: -8, weight: -1, increment: { min: -2, max: -2 } },
      maxHealth: { chance: -28, weight: -2, increment: { min: -3, max: -7 } },
      movement: { chance: -2, increment: { max: 1 } },
      maxRange: { chance: 3, weight: 1, increment: { max: 1 } },
      staminaMax: { chance: 2, weight: 1, increment: { min: -4, max: -6 } },
      ammoMax: { chance: 5, weight: -1, increment: { min: 1, max: 2 } },
      luck: { chance: 2, weight: 1, increment: { min: 1, max: 1 } },
    },
  },
  skyguard: {
    id: "skyguard",
    name: "Skyguard",
    family: UNIT_TAGS.VEHICLE,
    armorClass: ARMOR_CLASSES.MEDIUM,
    weaponClass: WEAPON_CLASSES.FLAK_CANNON,
    cost: 500,
    maxHealth: 100,
    attack: 72,
    armor: 55,
    movement: 5,
    minRange: 1,
    maxRange: 2,
    staminaMax: 80,
    ammoMax: 6,
    luck: 3,
    levelUpGrowthModifiers: {
      attack: { chance: -27, weight: -3, increment: { min: -2, max: -3 } },
      armor: { chance: 12, weight: 1, increment: { min: 1, max: 1 } },
      maxHealth: { chance: -8, increment: { min: -2, max: -5 } },
      maxRange: { chance: 2 },
      staminaMax: { chance: 2, weight: 1, increment: { min: 1, max: -1 } },
      ammoMax: { chance: 3, weight: -1, increment: { max: -1 } },
      luck: { chance: -8, weight: -2, increment: { max: -1 } },
    },
  },
  gunship: {
    id: "gunship",
    name: "Gunship",
    family: UNIT_TAGS.AIR,
    armorClass: ARMOR_CLASSES.AIR_LIGHT,
    weaponClass: WEAPON_CLASSES.ROCKET_PODS,
    cost: 900,
    maxHealth: 100,
    attack: 80,
    armor: 22,
    movement: 7,
    minRange: 1,
    maxRange: 1,
    staminaMax: 70,
    ammoMax: 7,
    luck: 3,
  },
  payload: {
    id: "payload",
    name: "Payload",
    family: UNIT_TAGS.AIR,
    armorClass: ARMOR_CLASSES.AIR_HEAVY,
    weaponClass: WEAPON_CLASSES.PAYLOAD_BOMBS,
    cost: 2200,
    maxHealth: 100,
    attack: 122,
    armor: 35,
    movement: 6,
    minRange: 1,
    maxRange: 2,
    staminaMax: 60,
    ammoMax: 6,
    luck: 2,
  },
  interceptor: {
    id: "interceptor",
    name: "Interceptor",
    family: UNIT_TAGS.AIR,
    armorClass: ARMOR_CLASSES.AIR_FAST,
    weaponClass: WEAPON_CLASSES.INTERCEPTOR_CANNONS,
    cost: 2000,
    maxHealth: 100,
    attack: 90,
    armor: 32,
    movement: 8,
    minRange: 1,
    maxRange: 1,
    staminaMax: 80,
    ammoMax: 7,
    luck: 3,
  },
  carrier: {
    id: "carrier",
    name: "Carrier",
    family: UNIT_TAGS.AIR,
    armorClass: ARMOR_CLASSES.AIR_HEAVY,
    weaponClass: null,
    cost: 300,
    maxHealth: 100,
    attack: 0,
    armor: 20,
    movement: 5,
    minRange: 0,
    maxRange: 0,
    staminaMax: 100,
    ammoMax: 0,
    luck: 0,
  },
};

export const BUILDING_RECRUITMENT = {
  barracks: ["grunt", "breaker", "longshot", "medic", "mechanic"],
  "motor-pool": ["runner", "bruiser", "juggernaut", "siege-gun", "skyguard"],
  airfield: ["gunship", "payload", "interceptor"],
};
